import { Node, Edge } from '@xyflow/react';

export const layoutAlgorithms = {
  hierarchical: (nodes: Node[], edges: Edge[]): Node[] => {
    const nodeMap = new Map<string, Node>();
    const levels = new Map<string, number>();
    const children = new Map<string, string[]>();
    
    // Build node map and children map
    nodes.forEach(node => {
      nodeMap.set(node.id, node);
      children.set(node.id, []);
    });
    
    // Build children relationships
    edges.forEach(edge => {
      const childList = children.get(edge.source) || [];
      childList.push(edge.target);
      children.set(edge.source, childList);
    });
    
    // Find root nodes (nodes with no incoming edges)
    const roots = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    );
    
    // Calculate levels using BFS
    const queue: string[] = roots.map(r => r.id);
    roots.forEach(root => levels.set(root.id, 0));
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentLevel = levels.get(current) || 0;
      
      const nodeChildren = children.get(current) || [];
      nodeChildren.forEach(child => {
        if (!levels.has(child)) {
          levels.set(child, currentLevel + 1);
          queue.push(child);
        }
      });
    }
    
    // Group nodes by level
    const levelGroups = new Map<number, string[]>();
    levels.forEach((level, nodeId) => {
      const group = levelGroups.get(level) || [];
      group.push(nodeId);
      levelGroups.set(level, group);
    });
    
    // Position nodes
    const horizontalSpacing = 250;
    const verticalSpacing = 150;
    
    return nodes.map(node => {
      const level = levels.get(node.id) || 0;
      const levelNodes = levelGroups.get(level) || [];
      const index = levelNodes.indexOf(node.id);
      const totalInLevel = levelNodes.length;
      
      return {
        ...node,
        position: {
          x: (index - (totalInLevel - 1) / 2) * horizontalSpacing,
          y: level * verticalSpacing
        }
      };
    });
  },

  radial: (nodes: Node[], edges: Edge[], centerNodeId?: string): Node[] => {
    if (nodes.length === 0) return nodes;
    
    const center = centerNodeId ? nodes.find(n => n.id === centerNodeId) : nodes[0];
    if (!center) return nodes;
    
    const levels = new Map<string, number>();
    const visited = new Set<string>();
    
    // BFS to determine distance from center
    const queue: [string, number][] = [[center.id, 0]];
    visited.add(center.id);
    levels.set(center.id, 0);
    
    while (queue.length > 0) {
      const [current, level] = queue.shift()!;
      
      edges.forEach(edge => {
        let neighbor: string | null = null;
        if (edge.source === current && !visited.has(edge.target)) {
          neighbor = edge.target;
        } else if (edge.target === current && !visited.has(edge.source)) {
          neighbor = edge.source;
        }
        
        if (neighbor) {
          visited.add(neighbor);
          levels.set(neighbor, level + 1);
          queue.push([neighbor, level + 1]);
        }
      });
    }
    
    // Group nodes by level
    const levelGroups = new Map<number, string[]>();
    levels.forEach((level, nodeId) => {
      const group = levelGroups.get(level) || [];
      group.push(nodeId);
      levelGroups.set(level, group);
    });
    
    // Position nodes in circles
    const radiusIncrement = 200;
    
    return nodes.map(node => {
      const level = levels.get(node.id) || 0;
      const levelNodes = levelGroups.get(level) || [];
      const index = levelNodes.indexOf(node.id);
      const totalInLevel = levelNodes.length;
      
      if (level === 0) {
        return { ...node, position: { x: 0, y: 0 } };
      }
      
      const angle = (index / totalInLevel) * 2 * Math.PI;
      const radius = level * radiusIncrement;
      
      return {
        ...node,
        position: {
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius
        }
      };
    });
  },

  grid: (nodes: Node[]): Node[] => {
    const columns = Math.ceil(Math.sqrt(nodes.length));
    const spacing = 250;
    
    return nodes.map((node, index) => ({
      ...node,
      position: {
        x: (index % columns) * spacing,
        y: Math.floor(index / columns) * spacing
      }
    }));
  }
};